import type {
  BackendDeviceStatus,
  Device,
  DeviceCapabilities,
  DeviceDiagnostics,
  DeviceHistoryEntry,
  DeviceStatus,
  DeviceTelemetryRecord,
  DeviceType,
  TelemetryPoint,
  CameraState,
  CameraOverallStatus,
} from '../types';
import { apiRequest } from './client';
import type { HostTelemetry } from './hostTelemetry';

/** Exact browser JSON shape of DeviceDto. Stream values are opaque secret references. */
export interface BackendDevice {
  id: string;
  name: string;
  ipAddress: string;
  isOnline: boolean;
  status: BackendDeviceStatus;
  deviceType: string;
  details?: string | null;
  dvrId?: string | null;
  channelNumber?: number | null;
  channelCount?: number | null;
  vendor?: string | null;
  mainStreamSecretReference?: string | null;
  subStreamSecretReference?: string | null;
  dvrIpAddress?: string | null;
  lastSeenUtc?: string | null;
  lastHeartbeatUtc?: string | null;
  capabilities?: DeviceCapabilities | null;
  healthScore?: number | null;
  diagnostics?: DeviceDiagnostics | null;
  monitoringObjectId?: string | null;
  onvifProfile?: string | null;
  supportsPtz?: boolean;
  streamQuality?: 'low' | 'balanced' | 'high';
  macAddress?: string | null;
  osVersion?: string | null;
  role?: string | null;
  location?: string | null;
  port?: number;
  monitoringObjectName?: string | null;
  updatedAtUtc?: string | null;
  cameraState?: CameraState | null;
  hostTelemetry?: HostTelemetry | null;
  isReservedChannel?: boolean;
}

export interface BackendDeviceState {
  id: string;
  isOnline: boolean;
  status: BackendDeviceStatus;
  healthScore: number;
  lastSeenUtc?: string | null;
  lastHeartbeatUtc?: string | null;
  updatedAtUtc?: string | null;
  diagnostics: DeviceDiagnostics;
  cameraState?: CameraState | null;
  hostTelemetry?: HostTelemetry | null;
  isReservedChannel?: boolean;
}

/** CreateDeviceRequest. Stream URLs are submitted only for Camera devices. */
export interface CreateDeviceRequest {
  deviceType: string;
  name: string;
  ipAddress: string;
  mainStreamUrl?: string;
  subStreamUrl?: string;
  onvifProfile?: string;
  supportsPtz?: boolean;
  dvrId?: string;
  channelNumber?: number;
  channelCount?: number;
  vendor?: string;
  macAddress?: string;
  osVersion?: string;
  role?: string;
  location?: string;
  monitoringObjectId?: string;
  port?: number;
}

export interface CreatedDeviceResponse {
  device: BackendDevice;
}

export interface CheckCameraConnectionRequest {
  ipAddress: string;
  port: number;
  streamUrl?: string;
}

export interface CameraConnectionCheckResponse {
  reachable: boolean;
  status: 'online' | 'offline' | 'unknown' | 'error';
  durationMs: number;
  message?: string | null;
}

/** UpdateDeviceRequest, including explicit replace/remove semantics for each stream. */
export interface UpdateDeviceRequest {
  name: string;
  ipAddress: string;
  port: number;
  monitoringObjectId?: string;
  replaceMainStreamUrl?: boolean;
  removeMainStreamUrl?: boolean;
  mainStreamUrl?: string;
  replaceSubStreamUrl?: boolean;
  removeSubStreamUrl?: boolean;
  subStreamUrl?: string;
  onvifProfile?: string;
  supportsPtz?: boolean;
  dvrId?: string;
  channelNumber?: number;
  channelCount?: number;
  vendor?: string;
  macAddress?: string;
  osVersion?: string;
  role?: string;
  location?: string;
}

export interface DeviceHistoryOptions {
  limit?: number;
}

export interface DeviceTelemetryOptions {
  limit?: number;
  from?: string;
  to?: string;
}

const typeMap: Record<string, DeviceType> = {
  camera: 'camera',
  dvr: 'recorder',
  nvr: 'recorder',
  computer: 'computer',
  server: 'server',
};

export const statusFromBackend = (status: BackendDeviceStatus, health?: number): DeviceStatus => {
  if (status === 3) return 'offline';
  if (status === 4) return 'critical';
  if (status === 5) return 'maintenance';
  if (status === 6) return 'disabled';
  if (status === 7) return 'updating';
  if (status === 8) return 'unauthorized';
  if (health != null && health < 45) return 'critical';
  if ((health != null && health < 75) || status === 0 || status === 1) return 'warning';
  return 'online';
};

const statusFromCameraOverall = (status: CameraOverallStatus): DeviceStatus => {
  switch (status) {
    case 'working': return 'online';
    case 'problem': return 'warning';
    case 'unavailable': return 'offline';
    case 'checking': return 'updating';
  }
};

const formatLastResponse = (value?: string | null) => {
  if (!value) return 'Нет данных';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return 'Нет данных';
  const diffMinutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
  if (diffMinutes < 1) return 'только что';
  if (diffMinutes < 60) return `${diffMinutes} мин назад`;
  const hours = Math.floor(diffMinutes / 60);
  return `${hours} ч назад`;
};

export const bitsToMegabits = (value?: number | null) =>
  value == null ? undefined : Math.round((value / 1_000_000) * 10) / 10;

export const bytesToMegabits = (value?: number | null) =>
  value == null ? undefined : Math.round(((value * 8) / 1_000_000) * 10) / 10;

const mapHostTelemetry = (sample: HostTelemetry) => ({
  cpu: sample.cpuPercent ?? undefined,
  ram: sample.ramPercent,
  uptimeHours: sample.uptimeSeconds / 3600,
  networkInMbps: bytesToMegabits(sample.networkReceiveBytesPerSecond),
  networkOutMbps: bytesToMegabits(sample.networkTransmitBytesPerSecond),
  cpuRecordedAtUtc: sample.recordedAtUtc,
});

/** Converts System.Text.Json's TimeSpan value (for example 1.02:03:04) into hours. */
export const dotNetTimeSpanToHours = (value: string): number | undefined => {
  const isoDuration = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/i.exec(value);
  if (isoDuration) {
    return (Number(isoDuration[1] || 0) + Number(isoDuration[2] || 0) / 60 + Number(isoDuration[3] || 0) / 3_600);
  }

  const duration = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2}(?:\.\d+)?)$/.exec(value);
  if (!duration) return undefined;
  return Number(duration[1] || 0) * 24 + Number(duration[2]) + Number(duration[3]) / 60 + Number(duration[4]) / 3_600;
};

export const mapBackendDevice = (device: BackendDevice): Device => {
  const health = device.healthScore == null ? 0 : Math.max(0, Math.min(100, Math.round(device.healthScore)));
  const type = typeMap[device.deviceType.toLowerCase()] ?? 'computer';
  const diagnostics = device.diagnostics ?? undefined;
  const cameraState = type === 'camera' ? device.cameraState ?? undefined : undefined;
  const isReservedChannel = type === 'camera' && (device.isReservedChannel ?? false);

  return {
    id: device.id,
    name: device.name,
    type,
    status: isReservedChannel ? 'disabled' : cameraState ? statusFromCameraOverall(cameraState.overallStatus) : statusFromBackend(device.status, device.healthScore == null ? undefined : health),
    backendStatus: device.status,
    // Keep the raw connectivity result separate from camera-state freshness.
    // CameraState.DeviceOnline intentionally becomes false when its heartbeat
    // expires, while DeviceDto.IsOnline still describes the last check result.
    isOnline: device.isOnline,
    health,
    ip: device.ipAddress,
    port: device.port ?? 0,
    location: device.location || device.monitoringObjectName || 'Расположение не указано',
    vendor: device.vendor || 'Не указан',
    model: device.onvifProfile || device.osVersion || device.details || 'Не указан',
    details: device.details ?? undefined,
    lastResponse: formatLastResponse(diagnostics?.lastResponseUtc ?? device.lastSeenUtc),
    lastSeen: device.lastSeenUtc || new Date(0).toISOString(),
    lastHeartbeatUtc: device.lastHeartbeatUtc ?? undefined,
    telemetry: {
      networkInMbps: bytesToMegabits(diagnostics?.downloadBytesPerSecond) ?? bitsToMegabits(diagnostics?.bandwidthBitsPerSecond),
      networkOutMbps: bytesToMegabits(diagnostics?.uploadBytesPerSecond),
      latencyMs: diagnostics?.latency,
      averageLatencyMs: diagnostics?.averageLatency,
      packetLoss: diagnostics?.packetLoss,
      jitterMs: diagnostics?.jitter,
      connectionQuality: diagnostics?.connectionQuality,
      heartbeat: type === 'computer' || type === 'server' ? (device.isOnline ? 100 : 0) : undefined,
      recordedAtUtc: diagnostics?.lastResponseUtc,
      ...(device.hostTelemetry ? mapHostTelemetry(device.hostTelemetry) : {}),
    },
    diagnostics,
    capabilities: device.capabilities ?? undefined,
    monitoringObjectId: device.monitoringObjectId ?? undefined,
    monitoringObjectName: device.monitoringObjectName ?? undefined,
    updatedAtUtc: device.updatedAtUtc ?? undefined,
    cameraState,
    camera:
      type === 'camera'
        ? {
            recorderId: device.dvrId || 'unassigned',
            recorderName: device.dvrIpAddress ? `DVR · ${device.dvrIpAddress}` : 'DVR не назначен',
            channel: device.channelNumber ?? 0,
            rtspAvailable: cameraState ? cameraState.rtsp === 'available' : (device.capabilities?.supportsRTSP ?? false),
            mainStreamSecretReference: device.mainStreamSecretReference ?? undefined,
            subStreamSecretReference: device.subStreamSecretReference ?? undefined,
            supportsPtz: device.supportsPtz ?? device.capabilities?.supportsPTZ ?? false,
            streamQuality: device.streamQuality ?? 'balanced',
            isReservedChannel,
            resolution: device.onvifProfile || 'Не указано',
            fps: cameraState?.fps ?? undefined,
          }
        : undefined,
    osVersion: device.osVersion ?? undefined,
    role: device.role ?? undefined,
    channelCount: device.channelCount ?? undefined,
  };
};

const normalizeLimit = (limit: number | undefined, max: number, fallback: number) =>
  Math.min(Math.max(Math.floor(limit ?? fallback), 1), max);

export const getDevices = async () => {
  const devices = await apiRequest<BackendDevice[]>('/api/devices');
  return devices.map(mapBackendDevice);
};

export const getDeviceStates = () => apiRequest<BackendDeviceState[]>('/api/devices/state');

export const applyDeviceState = (device: Device, state: BackendDeviceState): Device => {
  const diagnostics = state.diagnostics;
  const cameraState = state.cameraState ?? undefined;
  const isReservedChannel = device.type === 'camera' && (state.isReservedChannel ?? device.camera?.isReservedChannel ?? false);
  return {
    ...device,
    backendStatus: state.status,
    status: isReservedChannel ? 'disabled' : cameraState ? statusFromCameraOverall(cameraState.overallStatus) : statusFromBackend(state.status, state.healthScore),
    isOnline: state.isOnline,
    health: state.healthScore,
    lastResponse: formatLastResponse(diagnostics.lastResponseUtc ?? state.lastSeenUtc),
    lastSeen: state.lastSeenUtc || new Date(0).toISOString(),
    lastHeartbeatUtc: state.lastHeartbeatUtc ?? undefined,
    updatedAtUtc: state.updatedAtUtc ?? undefined,
    diagnostics,
    cameraState,
    camera: device.camera ? { ...device.camera, isReservedChannel, rtspAvailable: cameraState ? cameraState.rtsp === 'available' : device.camera.rtspAvailable, fps: cameraState?.fps ?? undefined } : undefined,
    telemetry: {
      ...device.telemetry,
      networkInMbps: bytesToMegabits(diagnostics.downloadBytesPerSecond) ?? bitsToMegabits(diagnostics.bandwidthBitsPerSecond),
      networkOutMbps: bytesToMegabits(diagnostics.uploadBytesPerSecond),
      latencyMs: diagnostics.latency,
      averageLatencyMs: diagnostics.averageLatency,
      packetLoss: diagnostics.packetLoss,
      jitterMs: diagnostics.jitter,
      connectionQuality: diagnostics.connectionQuality,
      recordedAtUtc: diagnostics.lastResponseUtc,
      ...(state.hostTelemetry ? mapHostTelemetry(state.hostTelemetry) : {}),
    },
  };
};

export const createDevice = async (request: CreateDeviceRequest) => {
  const created = await apiRequest<CreatedDeviceResponse>('/api/devices', {
    method: 'POST',
    body: request,
  });
  return mapBackendDevice(created.device);
};

export const deleteDevice = async (deviceId: string) => {
  await apiRequest<void>(`/api/devices/${encodeURIComponent(deviceId)}`, {
    method: 'DELETE',
  });
};

export const setCameraStreamQuality = async (cameraId: string,
  quality: 'low' | 'balanced' | 'high') => {
  const updated = await apiRequest<BackendDevice>(
    `/api/devices/${encodeURIComponent(cameraId)}/camera/stream-quality`, {
      method: 'PATCH', body: { quality },
    });
  return mapBackendDevice(updated);
};

export const setCameraReservation = async (cameraId: string, reserved: boolean) => {
  const updated = await apiRequest<BackendDevice>(
    `/api/devices/${encodeURIComponent(cameraId)}/camera/reservation`, {
      method: 'PUT', body: { reserved },
    });
  return mapBackendDevice(updated);
};

export const checkCameraConnection = (request: CheckCameraConnectionRequest) =>
  apiRequest<CameraConnectionCheckResponse>('/api/devices/camera/check-connection', {
    method: 'POST',
    body: request,
    timeoutMs: 8_000,
  });

export const checkDeviceEndpoint = (request: CheckCameraConnectionRequest) =>
  apiRequest<CameraConnectionCheckResponse>('/api/devices/check-endpoint', {
    method: 'POST',
    body: request,
    timeoutMs: 8_000,
  });

export const getDeviceHistory = (deviceId: string, options: DeviceHistoryOptions = {}) =>
  apiRequest<DeviceHistoryEntry[]>(
    `/api/devices/${encodeURIComponent(deviceId)}/history?limit=${normalizeLimit(options.limit, 1_000, 100)}`,
  );

export const getDeviceTelemetry = (deviceId: string, options: DeviceTelemetryOptions = {}) => {
  const query = new URLSearchParams({ limit: String(normalizeLimit(options.limit, 10_000, 300)) });
  if (options.from) query.set('from', options.from);
  if (options.to) query.set('to', options.to);
  return apiRequest<DeviceTelemetryRecord[]>(`/api/devices/${encodeURIComponent(deviceId)}/telemetry?${query}`);
};

export const checkDevice = async (deviceId: string) => {
  const device = await apiRequest<BackendDevice>(`/api/devices/${encodeURIComponent(deviceId)}/check`, {
    method: 'POST',
  });
  return mapBackendDevice(device);
};

interface TelemetryAccumulator {
  timestamp: string;
  cpu?: number;
  ram?: number;
  bandwidthBitsPerSecond?: number;
  uploadBytesPerSecond?: number;
  downloadBytesPerSecond?: number;
  uptimeSeconds?: number;
  healthScore?: number;
}

/** Groups the metric-per-row TelemetryPointDto response into chart samples. */
export const mapTelemetryRecords = (records: DeviceTelemetryRecord[]): TelemetryPoint[] => {
  const samples = new Map<string, TelemetryAccumulator>();

  for (const record of records) {
    const sample = samples.get(record.recordedAtUtc) ?? { timestamp: record.recordedAtUtc };
    switch (record.metricName.toLowerCase()) {
      case 'cpu':
        sample.cpu = record.value;
        break;
      case 'ram':
        sample.ram = record.value;
        break;
      case 'bandwidth':
        sample.bandwidthBitsPerSecond = record.value;
        break;
      case 'uploadspeed':
        sample.uploadBytesPerSecond = record.value;
        break;
      case 'downloadspeed':
        sample.downloadBytesPerSecond = record.value;
        break;
      case 'uptime':
        sample.uptimeSeconds = record.value;
        break;
      case 'healthscore':
        sample.healthScore = record.value;
        break;
      default:
        break;
    }
    samples.set(record.recordedAtUtc, sample);
  }

  return [...samples.values()]
    .sort((left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime())
    .map((sample) => {
      const timestamp = new Date(sample.timestamp);
      return {
        timestamp: sample.timestamp,
        label: Number.isNaN(timestamp.getTime())
          ? sample.timestamp
          : timestamp.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
        cpu: sample.cpu,
        ram: sample.ram,
        networkIn: bytesToMegabits(sample.downloadBytesPerSecond) ?? bitsToMegabits(sample.bandwidthBitsPerSecond),
        networkOut: bytesToMegabits(sample.uploadBytesPerSecond),
        health: sample.healthScore,
      };
    });
};
